import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  Paper,
  Container,
  useTheme,
  useMediaQuery,
  styled,
  Tooltip,
  IconButton,
} from "@mui/material";
import DevicesIcon from "@mui/icons-material/Devices";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  useGetCapabilitiesQuery,
  useModuleQuery,
  useProductQuery,
} from "../../useQuery";
import useGetAppsQuery from "../../useQuery/useGetAppsQuery";
import { useLocation } from "react-router-dom";
import { HelpIconButton } from "../../components-new/help/help-modal";
const StyledCard = ({ children, header, headerIcon }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.05)",
        borderRadius: "16px",
        border: "1px solid rgba(0, 0, 0, 0.03)",
        transition: "transform 0.2s, box-shadow 0.2s",
        overflow: "hidden",
        "&:hover": {
          boxShadow: "0px 15px 35px rgba(0, 0, 0, 0.08)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {header && (
        <Box
          sx={{
            p: 3,
            backgroundColor: "#f8f9fc",
            borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: "#2550d1", fontWeight: 600, display: "flex", alignItems: "center" }}
          >
            {headerIcon && <Box sx={{ mr: 1 }}>{headerIcon}</Box>}
            {header}
          </Typography>
        </Box>
      )}
      <Box sx={{ p: 3 }}>{children}</Box>
    </Paper>
  );
};

const ActionButton = ({ children, buttonType = "primary", ...props }) => (
  <Button
    {...props}
    sx={{
      borderRadius: "10px",
      padding: "8px 16px",
      textTransform: "none",
      fontWeight: 500,
      transition: "all 0.2s",
      boxShadow: buttonType === "primary" ? "0px 5px 15px rgba(37, 80, 209, 0.15)" : "none",
      backgroundColor: buttonType === "primary" ? "#2550d1" : "transparent",
      color: buttonType === "primary" ? "#fff" : "#2550d1",
      border: buttonType === "primary" ? "none" : "1px solid #d0d0d0",
      "&:hover": {
        backgroundColor: buttonType === "primary" ? "#1e3fa1" : "rgba(37, 80, 209, 0.05)",
        boxShadow: buttonType === "primary" ? "0px 8px 20px rgba(37, 80, 209, 0.25)" : "none",
        transform: "translateY(-2px)",
      },
      "&:disabled": {
        backgroundColor: buttonType === "primary" ? "rgba(0, 0, 0, 0.12)" : "transparent",
        color: "rgba(0, 0, 0, 0.26)",
      },
    }}
  >
    {children}
  </Button>
);

const FormField = ({ label, tooltip, children }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip}>
          <IconButton size="small" sx={{ ml: 0.5, color: "text.secondary" }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    {children}
  </Box>
);


const MobileAutomation = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const path = useLocation().pathname;


  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedCapability, setSelectedCapability] = useState("");
  const [selectedAppVersion, setSelectedAppVersion] = useState("");
  const [testScenarioName, setTestScenarioName] = useState("");
  const [error, setError] = useState("");
  const [token, setToken] = useState("");


  const { data: products } = useProductQuery();
  const { data: modules } = useModuleQuery(selectedProduct, {
    enabled: !!selectedProduct,
  });
  const { data: savedCapabilities } = useGetCapabilitiesQuery();
  const { data: apps, isLoading: appsLoading, error: appsError } = useGetAppsQuery(selectedProduct, {
    enabled: !!selectedProduct,
  });


  useEffect(() => {
    try {
      const userDetails = localStorage.getItem("userDetail");
      if (!userDetails) {
        setError("User details not found. Please log in again.");
        return;
      }
      const temp = JSON.parse(userDetails);
      setToken(temp.token);
    } catch (err) {
      setError("Error retrieving user details. Please log in again.");
    }
  }, []);


  useEffect(() => {
    setSelectedModule("");
    setSelectedCapability("");
    setSelectedAppVersion("");
  }, [selectedProduct]);


  useEffect(() => {
    setSelectedCapability("");
    setSelectedAppVersion("");
  }, [selectedModule]);


  const handleOpenAppium = () => {
    if (!token) {
      setError("Authentication token not found. Please log in again.");
      return;
    }

    if (!selectedProduct || !selectedModule || !testScenarioName.trim() || !selectedCapability || !selectedAppVersion) {
      setError("Please fill in all required fields before starting a new recording.");
      return;
    }

    try {
      const baseUrl = "http://localhost:5173";
      const params = new URLSearchParams({
        token,
        product: selectedProduct,
        module: selectedModule,
        testScenario: testScenarioName,
        continueRecording: "false",
        capabilityId: selectedCapability,
        appId: selectedAppVersion,
      });

      window.open(`${baseUrl}?${params.toString()}`, "_blank");
    } catch (e) {
      setError("Error processing capabilities. Please check the values.");
    }
  };


  const getCapabilityDisplayName = (capability) => {
    const capData = capability.capability || {};
    const platform = capData["platformName"] || "Unknown";
    const device = capData["deviceName"] || "Unknown Device";
    const version = capData["platformVersion"] || "Unknown Version";
    return `${capability.name || "Configuration"} (${platform} ${version} - ${device})`;
  };

  const getAppVersionDisplayName = (app) => {
    return `${app.appVersion} (${app.appLocation ? app.appLocation.split('/').pop() : 'Unknown Location'})`;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          background: "linear-gradient(135deg, #f1f5fd 0%, #e8eefa 100%)",
          borderRadius: "16px",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#2550d1",
              width: 60,
              height: 60,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0px 10px 20px rgba(37, 80, 209, 0.2)",
            }}
          >
            <DevicesIcon sx={{ fontSize: 32, color: "#fff" }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              sx={{
                color: "#2550d1",
                fontWeight: 700,
                mb: 0.5,
                fontSize: { xs: "1.75rem", md: "2.125rem" },
              }}
            >
              Nogrunt Mobile Automation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Configure device capabilities and run automated tests on your mobile applications
            </Typography>
          </Box>
        </Box>
        <HelpIconButton path={path} />
      </Paper>


      <StyledCard header="Test Scenario Configuration" headerIcon={<DevicesIcon />}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 3 }}>
          <FormField label="Select Product*">
            <Select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              displayEmpty
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "10px",
                },
              }}
            >
              <MenuItem disabled value="">
                Select Product
              </MenuItem>
              {products?.map((product) => (
                <MenuItem key={product.idproducts} value={product.idproducts}>
                  {product.productname}
                </MenuItem>
              ))}
            </Select>
          </FormField>

          <FormField label="Select Module*">
            <Select
              value={selectedModule}
              onChange={(e) => setSelectedModule(e.target.value)}
              displayEmpty
              fullWidth
              disabled={!selectedProduct}
              size="small"
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "10px",
                },
              }}
            >
              <MenuItem disabled value="">
                Select Module
              </MenuItem>
              {modules?.map((module) => (
                <MenuItem key={module.idmodules} value={module.idmodules}>
                  {module.modulename}
                </MenuItem>
              ))}
            </Select>
          </FormField>

          <FormField label="Select Device Capability*">
            <Select
              value={selectedCapability}
              onChange={(e) => setSelectedCapability(e.target.value)}
              displayEmpty
              fullWidth
              disabled={!selectedModule}
              size="small"
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "10px",
                },
              }}
            >
              <MenuItem disabled value="">
                Select Device Capability
              </MenuItem>
              {savedCapabilities?.map((capability) => (
                <MenuItem key={capability.CapId} value={capability.CapId}>
                  {getCapabilityDisplayName(capability)}
                </MenuItem>
              ))}
            </Select>
          </FormField>

          <FormField label="Select App Version*">
            <Select
              value={selectedAppVersion}
              onChange={(e) => setSelectedAppVersion(e.target.value)}
              displayEmpty
              fullWidth
              disabled={!selectedProduct || appsLoading}
              size="small"
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "10px",
                },
              }}
            >
              <MenuItem disabled value="">
                {appsLoading ? "Loading app versions..." : "Select App Version"}
              </MenuItem>
              {Array.isArray(apps) && apps.map((app) => (
                <MenuItem key={app.appId} value={app.appId}>
                  {getAppVersionDisplayName(app)}
                </MenuItem>
              ))}
            </Select>
          </FormField>

          <FormField label="Test Scenario Name*">
            <TextField
              value={testScenarioName}
              onChange={(e) => setTestScenarioName(e.target.value)}
              placeholder="Enter Test Scenario"
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />
          </FormField>
        </Box>


        <Box mt={2}>
          {selectedCapability && selectedAppVersion ? (
            <Alert
              severity="success"
              sx={{
                borderRadius: "10px",
                mb: 2,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography variant="body2">Device capability and app version selected and ready to use</Typography>
            </Alert>
          ) : (
            <Alert
              severity="info"
              sx={{
                borderRadius: "10px",
                mb: 2,
              }}
            >
              Please select a device capability and app version before starting the session
            </Alert>
          )}
        </Box>


        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          mt={4}
          flexDirection={isMobile ? "column" : "row"}
        >
          <ActionButton
            onClick={handleOpenAppium}
            disabled={!selectedModule || !testScenarioName.trim() || !selectedCapability || !selectedAppVersion}
            startIcon={<PlayArrowIcon />}
            buttonType="primary"
          >
            Start Automation Session
          </ActionButton>
        </Box>
      </StyledCard>


      {error && (
        <Alert
          severity="error"
          sx={{
            mt: 3,
            borderRadius: "10px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
          onClose={() => setError("")}
          variant="filled"
        >
          {error}
        </Alert>
      )}

      {appsError && (
        <Alert
          severity="error"
          sx={{
            mt: 3,
            borderRadius: "10px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
          onClose={() => {}}
          variant="filled"
        >
          Error loading app versions: {appsError.message || "Failed to fetch app versions"}
        </Alert>
      )}
    </Container>
  );
};

export default MobileAutomation;
